import { PREVIEW_QUERY_PARAMS, previewRouteParamKey } from './previewQueryParams';

export function normalizeRouteParams(routeParams: Record<string, string>): Record<string, string> {
  return Object.entries(routeParams).reduce<Record<string, string>>((acc, [key, value]) => {
    const normalizedValue = value.trim();
    if (normalizedValue !== '') {
      acc[key] = normalizedValue;
    }

    return acc;
  }, {});
}

export function updateUrlRouteParams(routeParams: Record<string, string>): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  for (const key of Array.from(url.searchParams.keys())) {
    if (key.startsWith(PREVIEW_QUERY_PARAMS.routeParamsPrefix)) {
      url.searchParams.delete(key);
    }
  }

  Object.entries(routeParams).forEach(([key, value]) => {
    url.searchParams.set(previewRouteParamKey(key), value);
  });

  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * The editor URL for a page, carrying the preview's route param values under the
 * shared `route_params[...]` convention. The single place that convention is
 * written, so the runtime's "Edit page" link and the editor's own preview visits
 * cannot drift apart.
 */
export function buildEditorUrl(pageId: string, routeParams: Record<string, string>): string {
  const base = `/admin/pages/${pageId}/editor`;
  const searchParams = new URLSearchParams();

  Object.entries(normalizeRouteParams(routeParams)).forEach(([key, value]) => {
    searchParams.set(previewRouteParamKey(key), value);
  });

  const query = searchParams.toString();

  return query ? `${base}?${query}` : base;
}

/**
 * Reads `route_params[...]` out of a URL. Accepts a path-relative URL (what
 * Inertia reports as the current page) as well as an absolute one.
 */
export function extractRouteParamsFromUrl(url?: string): Record<string, string> {
  if (typeof window === 'undefined' && !url) {
    return {};
  }

  const sourceUrl = url ?? window.location.href;
  const base = typeof window === 'undefined' ? 'http://localhost' : window.location.href;
  const searchParams = new URL(sourceUrl, base).searchParams;
  const extractedRouteParams: Record<string, string> = {};

  for (const [key, value] of searchParams.entries()) {
    const match = key.match(/^route_params\[([^\]]+)\]$/);

    if (!match) {
      continue;
    }

    const routeParamKey = match[1]?.trim();

    if (!routeParamKey) {
      continue;
    }

    extractedRouteParams[routeParamKey] = value;
  }

  return normalizeRouteParams(extractedRouteParams);
}
